<template>
  <div class="messenger-integration-layout">
    <Sidebar />

    <div class="main-content">
      <TopHeader />

      <main class="page-content">

        <div class="page-header">
          <div class="page-header-left">
            <div class="page-icon">
              <!-- Messenger lightning bolt icon -->
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.477 2 2 6.145 2 11.243c0 2.919 1.382 5.53 3.557 7.255V22l3.328-1.83A10.7 10.7 0 0012 20.486c5.523 0 10-4.144 10-9.243S17.523 2 12 2zm1.083 12.453L10.93 12.19l-4.194 2.263 4.61-4.9 2.216 2.263 4.144-2.263-4.623 4.9z"/>
              </svg>
            </div>
            <div>
              <h1 class="page-title">Messenger Integration</h1>
              <p class="page-subtitle">Connect your Facebook Pages to receive and reply to Messenger conversations and use automation flows.</p>
            </div>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <p>Loading connected pages...</p>
        </div>

        <div v-else class="connections-section">

          <!-- Info banner -->
          <div class="info-banner">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            <div>
              <strong>How it works</strong>
              <p>Click <strong>Connect Facebook Page</strong> to log in with Facebook and authorize your Pages. Once connected, Messenger conversations will appear in the inbox and you can trigger automation flows from them.</p>
            </div>
          </div>

          <!-- Connected Pages -->
          <div class="section-card">
            <div class="section-card-header">
              <h2>Connected Pages</h2>
              <button @click="connectMessenger" class="btn-connect" :disabled="connecting">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.477 2 2 6.145 2 11.243c0 2.919 1.382 5.53 3.557 7.255V22l3.328-1.83A10.7 10.7 0 0012 20.486c5.523 0 10-4.144 10-9.243S17.523 2 12 2z"/>
                </svg>
                {{ connecting ? 'Redirecting to Facebook...' : 'Connect Facebook Page' }}
              </button>
            </div>

            <div v-if="pages.length === 0" class="empty-state">
              <div class="empty-icon">
                <svg width="52" height="52" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.477 2 2 6.145 2 11.243c0 2.919 1.382 5.53 3.557 7.255V22l3.328-1.83A10.7 10.7 0 0012 20.486c5.523 0 10-4.144 10-9.243S17.523 2 12 2zm1.083 12.453L10.93 12.19l-4.194 2.263 4.61-4.9 2.216 2.263 4.144-2.263-4.623 4.9z"/>
                </svg>
              </div>
              <h3>No Facebook Pages connected yet</h3>
              <p>Click <strong>Connect Facebook Page</strong> above to authorize your Facebook Pages for Messenger.</p>
            </div>

            <div v-else class="pages-list">
              <div v-for="page in pages" :key="page.page_id" class="page-item">
                <div class="page-avatar">
                  <img v-if="page.page_pic_url" :src="page.page_pic_url" :alt="page.page_name" class="page-pic" />
                  <svg v-else width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.477 2 2 6.145 2 11.243c0 2.919 1.382 5.53 3.557 7.255V22l3.328-1.83A10.7 10.7 0 0012 20.486c5.523 0 10-4.144 10-9.243S17.523 2 12 2z"/>
                  </svg>
                </div>
                <div class="page-info">
                  <div class="page-name">{{ page.page_name }}</div>
                  <div v-if="page.page_category" class="page-category">{{ page.page_category }}</div>
                  <div class="page-meta">
                    <span class="page-id">ID: {{ page.page_id }}</span>
                    <span class="status-badge" :class="page.active == 1 ? 'status-active' : 'status-inactive'">
                      {{ page.active == 1 ? 'Active' : 'Inactive' }}
                    </span>
                  </div>
                  <div v-if="page.connected_at" class="page-date">Connected {{ formatDate(page.connected_at) }}</div>
                </div>
                <div class="page-actions">
                  <button
                    @click="disconnectConfirm(page)"
                    class="btn-disconnect"
                    :disabled="disconnecting === page.page_id"
                  >
                    {{ disconnecting === page.page_id ? 'Disconnecting...' : 'Disconnect' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Permissions explanation -->
          <div class="section-card">
            <h2>What permissions are requested?</h2>
            <div class="scopes-list">
              <div class="scope-item">
                <div class="scope-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.477 2 2 6.145 2 11.243c0 2.919 1.382 5.53 3.557 7.255V22l3.328-1.83A10.7 10.7 0 0012 20.486c5.523 0 10-4.144 10-9.243S17.523 2 12 2z"/>
                  </svg>
                </div>
                <div>
                  <strong>pages_messaging</strong>
                  <p>Send and receive messages on behalf of your Facebook Pages via Messenger.</p>
                </div>
              </div>
              <div class="scope-item">
                <div class="scope-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
                </div>
                <div>
                  <strong>pages_show_list</strong>
                  <p>List the Facebook Pages you manage so you can select which to connect.</p>
                </div>
              </div>
              <div class="scope-item">
                <div class="scope-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                </div>
                <div>
                  <strong>pages_read_engagement</strong>
                  <p>Read page engagement data needed to verify page ownership.</p>
                </div>
              </div>
              <div class="scope-item">
                <div class="scope-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                </div>
                <div>
                  <strong>pages_manage_metadata</strong>
                  <p>Subscribe your page to webhook events so messages arrive in real time.</p>
                </div>
              </div>
            </div>
            <div class="privacy-note">
              We store your page access tokens encrypted and only use them to send/receive Messenger messages.
              Read our <a href="https://quali-d.com/privacy-policy" target="_blank" rel="noopener">Privacy Policy</a>.
            </div>
          </div>

        </div>

        <!-- Disconnect Confirm Modal -->
        <div v-if="showDisconnectModal" class="modal-overlay" @click="showDisconnectModal = false">
          <div class="modal-content" @click.stop>
            <div class="modal-header">
              <h3>Disconnect Facebook Page</h3>
              <button @click="showDisconnectModal = false" class="btn-close">×</button>
            </div>
            <div class="modal-body">
              <p>Are you sure you want to disconnect <strong>{{ pendingDisconnect?.page_name }}</strong>?</p>
              <p class="modal-warning">Messenger automation flows for this page will stop working until you reconnect.</p>
            </div>
            <div class="modal-footer">
              <button @click="showDisconnectModal = false" class="btn-secondary">Cancel</button>
              <button @click="confirmDisconnect" class="btn-danger" :disabled="!!disconnecting">
                {{ disconnecting ? 'Disconnecting...' : 'Disconnect' }}
              </button>
            </div>
          </div>
        </div>

      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import { DashboardService } from '@/services/dashboard'

export default {
  name: 'MessengerIntegrationView',
  components: { Sidebar, TopHeader },

  data () {
    return {
      loading: true,
      pages: [],
      connecting: false,
      disconnecting: null,
      showDisconnectModal: false,
      pendingDisconnect: null
    }
  },

  mounted () {
    this.loadPages()
    this.handleOAuthCallback()
  },

  methods: {
    async loadPages () {
      this.loading = true
      try {
        const resp = await DashboardService.getMessengerPages()
        this.pages = resp.pages || []
      } catch (e) {
        this.$toast?.error('Failed to load connected pages')
      } finally {
        this.loading = false
      }
    },

    async connectMessenger () {
      this.connecting = true
      try {
        const resp = await DashboardService.getMessengerOAuthUrl()
        if (resp.success && resp.oauth_url) {
          window.location.href = resp.oauth_url
        } else {
          this.$toast?.error('Failed to get Facebook authorization URL')
          this.connecting = false
        }
      } catch (e) {
        this.$toast?.error('Failed to connect to Facebook')
        this.connecting = false
      }
    },

    disconnectConfirm (page) {
      this.pendingDisconnect = page
      this.showDisconnectModal = true
    },

    async confirmDisconnect () {
      if (!this.pendingDisconnect) return
      this.disconnecting = this.pendingDisconnect.page_id
      try {
        const resp = await DashboardService.disconnectMessengerPage(this.pendingDisconnect.page_id)
        if (resp.success) {
          this.pages = this.pages.filter(p => p.page_id !== this.pendingDisconnect.page_id)
          this.$toast?.success(`${this.pendingDisconnect.page_name} disconnected`)
        } else {
          this.$toast?.error(resp.message || 'Failed to disconnect page')
        }
      } catch (e) {
        this.$toast?.error('Failed to disconnect page')
      } finally {
        this.disconnecting = null
        this.showDisconnectModal = false
        this.pendingDisconnect = null
      }
    },

    handleOAuthCallback () {
      const params = new URLSearchParams(window.location.search)
      if (params.get('connected') === '1') {
        this.$toast?.success('Facebook Page connected successfully')
        window.history.replaceState({}, document.title, this.$route.path)
        this.loadPages()
      }
      if (params.get('error')) {
        const detail = params.get('detail') || params.get('error')
        this.$toast?.error('Facebook connection failed: ' + detail)
        window.history.replaceState({}, document.title, this.$route.path)
      }
    },

    formatDate (dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    }
  }
}
</script>

<style scoped>
.messenger-integration-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-primary, #111827);
  color: var(--text-primary, #f9fafb);
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 280px;
}

.page-content {
  flex: 1;
  overflow-y: auto;
  padding: 32px;
}

/* Page Header */
.page-header { margin-bottom: 28px; }

.page-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.page-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #0099FF, #0062E0);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
}

.page-title {
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 4px;
  color: var(--text-primary, #f9fafb);
}

.page-subtitle {
  margin: 0;
  color: var(--text-secondary, #9ca3af);
  font-size: 14px;
}

/* Info banner */
.info-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: rgba(0, 153, 255, 0.08);
  border: 1px solid rgba(0, 153, 255, 0.2);
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 20px;
  font-size: 13px;
  color: var(--text-primary, #f9fafb);
}

.info-banner svg { flex-shrink: 0; margin-top: 1px; color: #0099FF; }
.info-banner strong { display: block; margin-bottom: 3px; font-size: 13px; }
.info-banner p { margin: 0; color: var(--text-secondary, #9ca3af); }

/* Section cards */
.section-card {
  background: var(--card-background, #1f2937);
  border: 1px solid var(--border-color, #374151);
  border-radius: 12px;
  padding: 22px 24px;
  margin-bottom: 20px;
  max-width: 820px;
}

.section-card h2 {
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 18px;
  color: var(--text-primary, #f9fafb);
}

.section-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.section-card-header h2 { margin: 0; }

/* Connect button */
.btn-connect {
  display: flex;
  align-items: center;
  gap: 7px;
  background: #0099FF;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
}

.btn-connect:hover:not(:disabled) { background: #007acc; }
.btn-connect:disabled { opacity: 0.6; cursor: not-allowed; }

/* Empty state */
.empty-state {
  text-align: center;
  padding: 36px 20px;
  color: var(--text-secondary, #9ca3af);
}

.empty-icon { margin-bottom: 14px; opacity: 0.25; color: #0099FF; }
.empty-state h3 { font-size: 15px; font-weight: 600; margin: 0 0 7px; color: var(--text-primary, #f9fafb); }
.empty-state p { font-size: 13px; margin: 0; }

/* Pages list */
.pages-list { display: flex; flex-direction: column; gap: 10px; }

.page-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  background: var(--bg-secondary, #111827);
  border: 1px solid var(--border-color, #374151);
  border-radius: 10px;
}

.page-avatar {
  width: 44px;
  height: 44px;
  background: rgba(0, 153, 255, 0.12);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0099FF;
  flex-shrink: 0;
  overflow: hidden;
}

.page-pic {
  width: 44px;
  height: 44px;
  object-fit: cover;
  border-radius: 50%;
}

.page-info { flex: 1; min-width: 0; }
.page-name { font-size: 14px; font-weight: 600; color: var(--text-primary, #f9fafb); }
.page-category { font-size: 12px; color: var(--text-secondary, #9ca3af); margin-top: 2px; }

.page-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 5px;
  flex-wrap: wrap;
}

.page-id { font-size: 11px; color: var(--text-secondary, #6b7280); font-family: monospace; }

.status-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.status-active  { background: rgba(52, 211, 153, 0.15); color: #34d399; }
.status-inactive { background: rgba(239, 68, 68, 0.15); color: #ef4444; }

.page-date { font-size: 11px; color: var(--text-secondary, #6b7280); margin-top: 4px; }

.btn-disconnect {
  background: transparent;
  border: 1px solid var(--border-color, #374151);
  color: var(--text-secondary, #9ca3af);
  border-radius: 7px;
  padding: 6px 13px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}

.btn-disconnect:hover:not(:disabled) {
  border-color: #ef4444;
  color: #ef4444;
  background: rgba(239, 68, 68, 0.07);
}

.btn-disconnect:disabled { opacity: 0.5; cursor: not-allowed; }

/* Scopes */
.scopes-list { display: flex; flex-direction: column; gap: 14px; margin-bottom: 18px; }

.scope-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.scope-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: rgba(0, 153, 255, 0.12);
  color: #0099FF;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.scope-item strong { display: block; font-size: 13px; font-weight: 600; margin-bottom: 2px; color: var(--text-primary, #f9fafb); font-family: monospace; }
.scope-item p { margin: 0; font-size: 12px; color: var(--text-secondary, #9ca3af); }

.privacy-note {
  font-size: 12px;
  color: var(--text-secondary, #6b7280);
  border-top: 1px solid var(--border-color, #374151);
  padding-top: 14px;
}

.privacy-note a { color: #0099FF; text-decoration: none; }
.privacy-note a:hover { text-decoration: underline; }

/* Loading */
.loading-state { text-align: center; padding: 60px 20px; color: var(--text-secondary, #9ca3af); }

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border-color, #374151);
  border-top-color: #0099FF;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 14px;
}

@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 768px) {
  .main-content { margin-left: 0; }
  .page-content { padding: 16px; }
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9000;
}

.modal-content {
  background: var(--card-background, #1f2937);
  border: 1px solid var(--border-color, #374151);
  border-radius: 12px;
  width: 100%;
  max-width: 400px;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  border-bottom: 1px solid var(--border-color, #374151);
}

.modal-header h3 { margin: 0; font-size: 15px; font-weight: 600; color: var(--text-primary, #f9fafb); }

.btn-close {
  background: none;
  border: none;
  font-size: 20px;
  line-height: 1;
  color: var(--text-secondary, #9ca3af);
  cursor: pointer;
  padding: 0;
}

.modal-body {
  padding: 18px 22px;
  font-size: 14px;
  color: var(--text-primary, #f9fafb);
}

.modal-body p { margin: 0 0 8px; }
.modal-body p:last-child { margin-bottom: 0; }
.modal-warning { color: #f59e0b; font-size: 13px; }

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 9px;
  padding: 14px 22px;
  border-top: 1px solid var(--border-color, #374151);
}

.btn-secondary {
  background: transparent;
  border: 1px solid var(--border-color, #374151);
  color: var(--text-primary, #f9fafb);
  border-radius: 7px;
  padding: 7px 15px;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-secondary:hover { background: rgba(255, 255, 255, 0.05); }

.btn-danger {
  background: #ef4444;
  border: none;
  color: white;
  border-radius: 7px;
  padding: 7px 15px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-danger:hover:not(:disabled) { background: #dc2626; }
.btn-danger:disabled { opacity: 0.6; cursor: not-allowed; }
</style>
